import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PremiumCard } from "../components/PremiumCard";
import { PremiumButton } from "../components/PremiumButton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { fadeIn, slideUp, staggerContainer } from "../animations";
import { Card, CardContent } from "../components/ui/card";
import * as Icons from "lucide-react";

export const B2BLaptopAnkauf = () => {
  const [refVorteile, isVorteileVisible] = useScrollReveal({ once: true });
  const [refGeraete, isGeraeteVisible] = useScrollReveal({ once: true });
  const [refAblauf, isAblaufVisible] = useScrollReveal({ once: true });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section - Apple Style */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#143e6e] via-[#1f7db6] to-[#143e6e]" />
        
        {/* Animated gradient orbs */}
        <motion.div
          className="absolute top-20 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-20 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        />

        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h1 variants={slideUp} className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
            Firmen-Laptop-Ankauf
          </motion.h1>
          <motion.p variants={slideUp} className="text-xl sm:text-2xl text-white/90 mb-10 max-w-3xl mx-auto">
            Professioneller Ankauf gebrauchter Business-Laptops in größeren Mengen
          </motion.p>
          <motion.div variants={fadeIn}>
            <Link to="/request-quote">
              <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                Jetzt Angebot anfragen
              </PremiumButton>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Vorteile - Apple Style */}
      <section ref={refVorteile} className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            animate={isVorteileVisible ? "visible" : "hidden"}
            variants={slideUp}
            className="text-4xl sm:text-5xl font-bold text-[#143e6e] text-center mb-16"
          >
            Warum Firmen-Laptops bei uns verkaufen?
          </motion.h2>
          
          <motion.div
            initial="hidden"
            animate={isVorteileVisible ? "visible" : "hidden"}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { icon: Icons.ShieldCheck, title: "Zertifizierte Datenlöschung", desc: "DSGVO-konforme Löschung mit Zertifikat" },
              { icon: Icons.Truck, title: "Kostenlose Abholung", desc: "Deutschlandweite Logistik inklusive" },
              { icon: Icons.Clock, title: "48h Erstprüfung", desc: "Schnelle Bewertung Ihrer Geräte" },
              { icon: Icons.Euro, title: "Faire Preise", desc: "Marktgerechte Ankaufswerte" }
            ].map((item, idx) => (
              <motion.div key={idx} variants={slideUp}>
                <PremiumCard className="p-8 text-center h-full">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#1f7db6] to-[#143e6e] rounded-2xl flex items-center justify-center"
                  >
                    <item.icon className="text-white" size={32} />
                  </motion.div>
                  <h3 className="font-bold text-xl mb-3 text-[#143e6e]">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </PremiumCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Angekaufte Geräte */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#143e6e] text-center mb-12">
            Welche Firmen-Laptops kaufen wir an?
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {['Dell', 'Lenovo', 'HP', 'Apple', 'Fujitsu', 'Acer', 'Asus', 'Microsoft'].map((brand) => (
              <div key={brand} className="text-center">
                <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-[#1f7db6] transition-colors">
                  <p className="font-semibold text-lg text-gray-800">{brand}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 max-w-3xl mx-auto">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6">
                <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                  <Icons.Info className="text-[#1f7db6]" size={20} />
                  Mindestmenge
                </h3>
                <p className="text-gray-700">
                  Wir kaufen Firmen-Laptops ab einer Mindestmenge von <strong>20 Geräten</strong>. 
                  Bei größeren Mengen (50+, 100+, 500+) bieten wir besonders attraktive Konditionen.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#143e6e] text-center mb-12">
            So läuft der Ankauf ab
          </h2>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              { step: 1, title: "Anfrage stellen", desc: "Füllen Sie unser Anfrageformular aus oder kontaktieren Sie uns direkt" },
              { step: 2, title: "Bestandsprüfung", desc: "Wir prüfen Ihre Geräteliste und erstellen eine erste Bewertung" },
              { step: 3, title: "Angebot erhalten", desc: "Sie erhalten innerhalb von 48h ein unverbindliches Angebot" },
              { step: 4, title: "Abholung vereinbaren", desc: "Nach Ihrer Zusage holen wir die Geräte kostenlos ab" },
              { step: 5, title: "Finale Prüfung", desc: "Detaillierte Prüfung und DSGVO-konforme Datenlöschung" },
              { step: 6, title: "Auszahlung", desc: "Schnelle Überweisung nach finaler Prüfung" }
            ].map((item) => (
              <div key={item.step} className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#1f7db6] text-white rounded-full flex items-center justify-center font-bold">
                    {item.step}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#143e6e] text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Bereit, Ihre Firmen-Laptops zu verkaufen?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Fordern Sie jetzt ein unverbindliches Angebot an
          </p>
          <Link to="/request-quote">
            <Button size="lg" className="bg-white text-[#143e6e] hover:bg-gray-100 font-semibold">
              Firmenangebot anfordern
              <Icons.ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
